import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

const PRESET_COLORS = ["#1C352D", "#E2725B", "#5B7FE2", "#A65BE2", "#E2A65B", "#5BE2A6", "#8B5A3C", "#556B2F"];
const EMPTY = { name: "", color: "#1C352D", coordinator_teacher_id: "", description: "", order: 0 };

export default function AreaFormDialog({ open, onOpenChange, mode = "create", initial, onSubmit, submitting }) {
  const [form, setForm] = useState(EMPTY);
  const { data: teachers = [] } = useQuery({
    queryKey: ["teachers-for-area"],
    queryFn: async () => (await api.get("/teachers")).data,
    enabled: open,
  });

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY, ...initial, coordinator_teacher_id: initial.coordinator_teacher_id || "" } : EMPTY);
  }, [open, initial]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  function submit(e) {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.coordinator_teacher_id) payload.coordinator_teacher_id = null;
    if (!payload.description) payload.description = null;
    onSubmit(payload);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Nuova area disciplinare" : "Modifica area"}</DialogTitle>
          <DialogDescription>Le aree raggruppano i corsi per disciplina e possono avere un coordinatore.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs">Nome area</Label>
            <Input data-testid="area-name" value={form.name} onChange={(e) => set("name", e.target.value)} required placeholder="es. Teologia Sistematica" />
          </div>

          <div>
            <Label className="text-xs">Colore</Label>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {PRESET_COLORS.map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => set("color", c)}
                  data-testid={`area-color-${c.slice(1)}`}
                  className={`w-8 h-8 rounded-full border-2 transition-transform duration-150 hover:scale-110 ${form.color === c ? "ring-2 ring-offset-2 ring-[#1C352D] border-white" : "border-white"}`}
                  style={{ background: c }}
                />
              ))}
              <Input className="w-24 h-9 ml-2" value={form.color || ""} onChange={(e) => set("color", e.target.value)} />
            </div>
          </div>

          <div>
            <Label className="text-xs">Coordinatore</Label>
            <Select value={form.coordinator_teacher_id || "__none"} onValueChange={(v) => set("coordinator_teacher_id", v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="area-coordinator"><SelectValue placeholder="Nessun coordinatore" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Nessuno</SelectItem>
                {teachers.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`area-coord-opt-${t.id}`}>
                    {t.first_name} {t.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Descrizione</Label>
            <Textarea rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} />
          </div>

          <div>
            <Label className="text-xs">Ordine visualizzazione</Label>
            <Input type="number" value={form.order ?? 0} onChange={(e) => set("order", parseInt(e.target.value) || 0)} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={submitting} data-testid="area-submit" className="bg-[#1C352D] hover:bg-[#264A3E]">
              {submitting ? "Salvataggio…" : mode === "create" ? "Crea area" : "Salva"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
